import React, { useMemo, useState } from 'react';
import {
  Clock,
  Webhook,
  Radio,
  Check,
  AlertTriangle,
  Search,
  CalendarPlus,
} from 'lucide-react';
import { cn } from '@cotera/client/v0/lib/utils';
import { DATASET_TRIGGER_CRITERION_LABELS } from '@cotera/sdk/core';
import { humanReadableSchedule } from '../../trigger/schedule-utils';
import {
  useTriggerSummaries,
  type WorkflowTriggerOption,
} from '../../trigger/hooks';
import { Icon } from '@cotera/client/app/components/ui';

export type TriggerSelection =
  | { type: 'existing'; triggerId: string }
  | { type: 'new-cron' }
  | { type: 'new-webhook' }
  | { type: 'new-event' }
  | { type: 'new-dataset' };

function selectionEquals(
  a: TriggerSelection | null,
  b: TriggerSelection
): boolean {
  if (a === null) {
    return false;
  }
  if (a.type !== b.type) {
    return false;
  }
  if (a.type === 'existing' && b.type === 'existing') {
    return a.triggerId === b.triggerId;
  }
  return true;
}

/**
 * Trigger picker body (existing triggers first, with how many workflows share
 * each, then "create new" below a divider + a duplication nudge). Controlled
 * selection so a parent can host it in a side panel and confirm on Save.
 *
 * With `showCreateNew={false}` it renders just the searchable list of existing
 * triggers — for hosts (e.g. the add-trigger modal) that keep "create new"
 * behind their own chooser and only drill into this to reuse an existing one.
 */
export const TriggerPickerContent: React.FC<{
  existingTriggers: WorkflowTriggerOption[];
  selection: TriggerSelection | null;
  onSelectionChange: (selection: TriggerSelection) => void;
  showCreateNew?: boolean;
}> = ({
  existingTriggers,
  selection,
  onSelectionChange,
  showCreateNew = true,
}) => {
  const [query, setQuery] = useState('');

  const triggerIds = useMemo(
    () => existingTriggers.map((t) => t.id),
    [existingTriggers]
  );
  const { summaries } = useTriggerSummaries(triggerIds);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (q === '') {
      return existingTriggers;
    }
    return existingTriggers.filter(
      (t) =>
        t.name.toLowerCase().includes(q) ||
        (t.schedule ?? '').toLowerCase().includes(q)
    );
  }, [existingTriggers, query]);

  const isNewSelected = selection !== null && selection.type !== 'existing';

  const detailFor = (trigger: WorkflowTriggerOption): string => {
    const count = summaries.get(trigger.id)?.workflowCount;
    const base =
      trigger.kind === 'webhook'
        ? 'Webhook'
        : trigger.kind === 'event'
        ? trigger.eventName !== undefined &&
          trigger.eventName !== null &&
          trigger.eventName !== ''
          ? trigger.eventName
          : 'Event'
        : trigger.kind === 'dataset'
        ? trigger.criterion !== undefined && trigger.criterion !== null
          ? `${DATASET_TRIGGER_CRITERION_LABELS[trigger.criterion]}`
          : 'Dataset'
        : trigger.schedule !== null && trigger.schedule !== ''
        ? humanReadableSchedule(trigger.schedule)
        : 'Schedule';
    if (count === undefined || count === 0) {
      return base;
    }
    return `${base} · ${count} workflow${count === 1 ? '' : 's'} attached`;
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search schedules and webhooks..."
          className="h-9 w-full rounded-md border bg-background pl-9 pr-3 text-sm outline-none focus:ring-1 focus:ring-ring"
        />
      </div>

      {filtered.length > 0 ? (
        <div>
          {showCreateNew ? (
            <p className="px-1 pb-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              Existing triggers
            </p>
          ) : null}
          <div className="flex flex-col gap-1">
            {filtered.map((trigger) => {
              const selected = selectionEquals(selection, {
                type: 'existing',
                triggerId: trigger.id,
              });
              return (
                <button
                  key={trigger.id}
                  type="button"
                  onClick={() =>
                    onSelectionChange({
                      type: 'existing',
                      triggerId: trigger.id,
                    })
                  }
                  className={cn(
                    'flex items-center gap-2 rounded-md border px-3 py-2 text-left',
                    selected
                      ? 'border-primary bg-primary/5'
                      : 'border-transparent hover:bg-muted'
                  )}
                >
                  {trigger.kind === 'webhook' ? (
                    <Webhook className="h-4 w-4 shrink-0 text-muted-foreground" />
                  ) : trigger.kind === 'event' ? (
                    <Radio className="h-4 w-4 shrink-0 text-muted-foreground" />
                  ) : trigger.kind === 'dataset' ? (
                    <Icon
                      icon={'dataset'}
                      className="h-4 w-4 shrink-0 text-muted-foreground"
                    />
                  ) : (
                    <Clock className="h-4 w-4 shrink-0 text-muted-foreground" />
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium">
                      {trigger.name}
                    </p>
                    <p className="truncate text-xs text-muted-foreground">
                      {detailFor(trigger)}
                    </p>
                  </div>
                  {selected ? (
                    <Check className="h-4 w-4 shrink-0 text-primary" />
                  ) : null}
                </button>
              );
            })}
          </div>
        </div>
      ) : null}

      {!showCreateNew && filtered.length === 0 ? (
        <p className="px-1 py-6 text-center text-sm text-muted-foreground">
          {existingTriggers.length === 0
            ? 'No existing triggers yet.'
            : 'No triggers match your search.'}
        </p>
      ) : null}

      {showCreateNew ? (
        <div>
          <p className="px-1 pb-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground/70">
            Create new
          </p>
          <div className="flex flex-col gap-1">
            <button
              type="button"
              onClick={() => onSelectionChange({ type: 'new-cron' })}
              className={cn(
                'flex items-center gap-2 rounded-md border px-3 py-2 text-left',
                selectionEquals(selection, { type: 'new-cron' })
                  ? 'border-primary bg-primary/5'
                  : 'border-transparent hover:bg-muted'
              )}
            >
              <CalendarPlus className="h-4 w-4 shrink-0 text-muted-foreground/70" />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm text-muted-foreground">
                  New cron schedule…
                </p>
                <p className="truncate text-xs text-muted-foreground/70">
                  Set a custom repeat interval
                </p>
              </div>
            </button>
            <button
              type="button"
              onClick={() => onSelectionChange({ type: 'new-webhook' })}
              className={cn(
                'flex items-center gap-2 rounded-md border px-3 py-2 text-left',
                selectionEquals(selection, { type: 'new-webhook' })
                  ? 'border-primary bg-primary/5'
                  : 'border-transparent hover:bg-muted'
              )}
            >
              <Webhook className="h-4 w-4 shrink-0 text-muted-foreground/70" />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm text-muted-foreground">
                  New app event…
                </p>
                <p className="truncate text-xs text-muted-foreground/70">
                  Generate an inbound event URL
                </p>
              </div>
            </button>
            <button
              type="button"
              onClick={() => onSelectionChange({ type: 'new-dataset' })}
              className={cn(
                'flex items-center gap-2 rounded-md border px-3 py-2 text-left',
                selectionEquals(selection, { type: 'new-dataset' })
                  ? 'border-primary bg-primary/5'
                  : 'border-transparent hover:bg-muted'
              )}
            >
              <Icon
                icon={'dataset'}
                className="h-4 w-4 shrink-0 text-muted-foreground/70"
              />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm text-muted-foreground">
                  New dataset change…
                </p>
                <p className="truncate text-xs text-muted-foreground/70">
                  Run for each row added, changed or removed
                </p>
              </div>
            </button>
            <button
              type="button"
              onClick={() => onSelectionChange({ type: 'new-event' })}
              className={cn(
                'flex items-center gap-2 rounded-md border px-3 py-2 text-left',
                selectionEquals(selection, { type: 'new-event' })
                  ? 'border-primary bg-primary/5'
                  : 'border-transparent hover:bg-muted'
              )}
            >
              <Radio className="h-4 w-4 shrink-0 text-muted-foreground/70" />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm text-muted-foreground">
                  New workflow event…
                </p>
                <p className="truncate text-xs text-muted-foreground/70">
                  Run when another workflow emits a named event
                </p>
              </div>
            </button>
          </div>
        </div>
      ) : null}

      {showCreateNew && isNewSelected && existingTriggers.length > 0 ? (
        <div className="flex items-start gap-2 rounded-md border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-xs text-amber-700">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            Creating a new trigger when a matching one exists can duplicate
            schedules. Check the existing list above — reuse one to keep
            workflows in sync.
          </span>
        </div>
      ) : null}
    </div>
  );
};
